<template>
  <div :class="'md-layout'">
    <div :class="'md-layout-item md-size-100'">
      <md-card>
        <md-card-header :class="'md-card-header-icon md-card-header-danger'">
          <div :class="'card-icon'">
            <md-icon>assignment</md-icon>
          </div>
          <h4 :class="'title'">
            List
            <md-button :class="'md-danger pull-right'" @click.prevent="isTryingToCreate = true"
              v-if="cannotView() && clusters.length > 0">
              New
            </md-button>
          </h4>
        </md-card-header>
        <!--Table-->
        <md-card-content>
          <md-table v-model="clusters" table-header-color="orange">
            <md-empty-state md-icon="list" md-label="Clusters" :md-description="'No cluster found'" v-if="!isFetching">
              <md-button v-if="cannotView()" :class="'md-primary md-raised'" @click.prevent="isTryingToCreate = true">
                New
              </md-button>
            </md-empty-state>
            <md-table-row slot="md-table-row" slot-scope="{ item: c }">
              <md-table-cell md-label="RQR Type">{{ c.name }}</md-table-cell>
              <md-table-cell v-if="cannotView()" md-label="Actions" md-numeric>
                <md-button :class="'md-simple md-just-icon'" :md-ripple="false" @click.prevent="tryToEdit(c)" 
                  title="Edit cluster">
                  <md-icon>edit</md-icon>
                </md-button>
                <md-button :class="'md-simple md-just-icon'" :md-ripple="false" @click.prevent="tryToDelete(c)"
                  title="Delete cluster">
                  <md-icon>delete</md-icon>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
          <div :class="'md-flex text-center'" v-if="clusters.length > 0">
            <Pagination :total="pagination.totalItems" :value="pagination.currentPage"
              :perPage="pagination.perPage" @input="changePage"/>
          </div>
          <div :class="'md-flex text-center'" v-if="isFetching">
            <md-progress-spinner :class="'md-accent'" :md-stroke="3" md-mode="indeterminate"></md-progress-spinner>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <md-dialog :md-active.sync="isTryingToDelete">
      <md-dialog-title>Delete cluster</md-dialog-title>
      <md-dialog-content>
        Are you sure you want to delete <b>{{ deletableCluster.name }}</b>?
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isDeletingCluster" @click.prevent="dismissDeleteModal">Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="isDeletingCluster" @click.prevent="deleteCluster(deletableCluster)">
          {{ !isDeletingCluster ? "Delete" : "Deleting..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <!--Create-->
    <md-dialog :md-active.sync="isTryingToCreate">
      <md-dialog-title>Create cluster</md-dialog-title>
      <md-dialog-content>
        <div :class="'md-layout'">
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="section_description">Name</label>
              <md-input id="name" v-model="newCluster.name" :disabled="isCreatingCluster"/>
            </md-field>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isCreatingCluster" @click.prevent="dismissCreatingModal">Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="isCreatingCluster || !isValidForm" @click.prevent="createCluster">
          {{ !isCreatingCluster ? "Create" : "Creating..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <!--Edit-->
    <md-dialog :md-active.sync="isTryingToEdit">
      <md-dialog-title>Edit cluster</md-dialog-title>
      <md-dialog-content>
        <div :class="'md-layout'">
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="name">Name</label>
              <md-input id="section_description" v-model="editableCluster.name" :disabled="isEditingCluster"/>
            </md-field>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isEditingCluster" @click.prevent="dismissEditModal" >Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="$v.editableCluster.$invalid || isEditingCluster"
          @click.prevent="editCluster">
          {{ !isEditingCluster ? "Update" : "Updating..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { maxLength, required } from "vuelidate/lib/validators";
import { date, rqrType, complianceAndQuality } from "@/filters";
import { Pagination } from "@/components";
import { camelToSnake } from "@/methods/api/requests"
import * as utils from "@/utils"
import rqrTypeEnum from "@/enums/rqrType"

export default {
  created() {
    this.getClusters();
  },

  data() {
    return {
      clusters: [],
      isFetching: false,
      isTryingToDelete: false,
      deletableCluster: {},
      isDeletingCluster: false,
      isValidForm: false,
      isTryingToCreate: false,
      isCreatingCluster: false,
      newCluster: {
        name: "",
      },
      isTryingToEdit: false,
      editableCluster: { name: "" },
      isEditingCluster: false,
      edit: {
        rqr_type: rqrTypeEnum.COMPLICE,
        section: "overall",
      },
      pagination: {
        totalItems: "",
        currentPage: "",
        perPage: "",
      },
      errorResponses: {
        400: "The users spreadsheet is invalid",
        422: "There is an error with your file",
      },
    };
  },

  components: {
    Pagination,
  },

  computed: {
    paginationQuery() {
      return utils.getPaginationQuery(this.pagination.currentPage);
    },
  },

  filters: {
    date,
    rqrType,
    complianceAndQuality,
  },

  methods: {
    cannotView() {
      return utils.getCannotView(this.$store.state.currentProfile)
    },

    changePage(toPage) {
      if (toPage === this.pagination.currentPage)
        return;      

      this.pagination.currentPage = toPage;
      this.getClusters();
    },

    createCluster() {
      if (this.isCreatingCluster)
        return;     

      this.$api.request
        .post(`clusters`, this.newCluster)
        .then(() => {
          this.dismissCreatingModal();
          this.isCreatingCluster = false;
          this.getClusters();
          this.$toast.show(
            `<b>Cluster</b> was created successfully`,
            "The cluster has been created successfully"
          );
        })
        .catch((response) => {
          this.$toast.show(`<b>Error</b>`, response.data.message);
        })
        .finally(() => {
          this.isCreatingCluster = false;
        });
    },

    deleteCluster() {
      if (this.isDeletingCluster)
        return;

      this.isDeletingUser = true;
      this.$api.request
        .delete(`clusters/${this.deletableCluster.id}`)
        .then(() => {
          this.isDeletingCluster = false;
          this.$toast.show(
            `<b>Cluster</b> was deleted successfully`,
            "The cluster has been deleted successfully"
          );
          this.getClusters();
        })
        .catch((error) => {
          this.$toast.warning(error.response.data.message, "");
        })
        .finally(() => {
          this.isDeletingCluster = false;
          this.dismissDeleteModal();
        });
    },

    dismissCreatingModal() {
      if (this.isCreatingCluster)
        return;

      this.isTryingToCreate = false;
      this.selectedOptions = [];
      this.newCluster = {
        name: "",
      };
      this.$v.newCluster.$reset();
    },

    dismissDeleteModal() {
      if (this.isDeletingCluster)
        return;

      this.isTryingToDelete = false;
    },

    dismissEditModal() {
      if (this.isEditingCluster)
        return;      

      this.isTryingToEdit = false;
      this.selectedOptions = [];
    },

    editCluster() {
      if (this.isEditingCluster)
        return;

      this.isEditingCluster = true;
      this.$api.request
        .put(`clusters/${this.editableCluster.id}`, this.editableCluster)
        .then(() => {
          this.isEditingCluster = false;
          this.getClusters();
          this.$toast.show(
            `<b>Cluster</b> was updated successfully`,
            "The cluster has been updated successfully"
          );
        })
        .catch((error) => {
          this.$toast.warning(error.response.data.message, "");
        })
        .finally(() => {
          this.isEditingCluster = false;
          this.dismissEditModal();
        });
    },

    getClusters() {
      this.isFetching = true;
      this.$api
        .request(`clusters?${this.paginationQuery}`)
        .then((response) => {
          this.clusters = camelToSnake(response.data.data);
          this.pagination.totalItems = response.data.meta.total;
          this.pagination.currentPage = response.data.meta.currentPage;
          this.pagination.perPage = response.data.meta.perPage;
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    tryToDelete(cluster) {
      this.isTryingToDelete = true;
      this.deletableCluster = cluster;
    },

    tryToEdit(cluster) {
      this.isTryingToEdit = true;
      this.editableCluster = Object.assign({}, cluster);
      this.edit.rqr_type = cluster.rqr_type;
      this.edit.section = cluster.section;
      this.edit.first = true;
      this.options = cluster.options;
      this.options.forEach((opt) => this.selectedOptions.push(opt.id));
    },

    validateNewCluster() {
      this.isValidForm = !this.$v.newCluster.$invalid;
    },
  },

  validations: {
    editableCluster: {
      name: {
        required,
        maxLength: maxLength(254),
      },
    },

    newCluster: {
      name: {
        required,
        maxLength: maxLength(254),
      },
    },
  },

  watch: {
    "$v.newCluster.$invalid": function () {
      this.validateNewCluster();
    },
  },
};
</script>
